import { useState } from 'react'

const fieldClass =
  'rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-100'

const btnPrimary =
  'inline-flex items-center rounded-lg bg-gray-900 px-3.5 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60'

/**
 * Simple add/upsert panel for mapping tabs (P-line, Luminate).
 *
 * fields: Array<{
 *   name, label, type?: 'text'|'select'|'checkbox',
 *   options?: {value,label}[], placeholder?, required?
 * }>
 * onSubmit(values) => Promise
 */
export default function MappingAddForm({
  title = 'Add / update',
  fields,
  submitLabel = 'Save',
  onSubmit,
  initialValues = {},
}) {
  const [values, setValues] = useState(() => {
    const seed = {}
    for (const f of fields) {
      if (f.type === 'checkbox') seed[f.name] = Boolean(initialValues[f.name])
      else seed[f.name] = initialValues[f.name] ?? f.defaultValue ?? ''
    }
    return seed
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [okMsg, setOkMsg] = useState(null)

  function setField(name, value) {
    setValues((prev) => ({ ...prev, [name]: value }))
    setError(null)
    setOkMsg(null)
  }

  async function handleSubmit(event) {
    event.preventDefault()
    for (const f of fields) {
      if (f.required && f.type !== 'checkbox') {
        const v = String(values[f.name] ?? '').trim()
        if (!v) {
          setError(`${f.label} is required`)
          return
        }
      }
    }
    setSaving(true)
    setError(null)
    setOkMsg(null)
    try {
      await onSubmit(values)
      setOkMsg('Saved')
      const cleared = { ...values }
      for (const f of fields) {
        if (f.clearOnSuccess !== false && f.type !== 'checkbox' && f.type !== 'select') {
          cleared[f.name] = ''
        }
      }
      setValues(cleared)
    } catch (err) {
      setError(err?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm"
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-gray-900">{title}</p>
        {okMsg ? <span className="text-xs font-medium text-emerald-700">{okMsg}</span> : null}
      </div>
      <div className="flex flex-wrap items-end gap-3">
        {fields.map((f) => {
          if (f.type === 'checkbox') {
            return (
              <label key={f.name} className="flex items-center gap-2 pb-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={Boolean(values[f.name])}
                  onChange={(e) => setField(f.name, e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                {f.label}
              </label>
            )
          }
          if (f.type === 'select') {
            return (
              <label key={f.name} className="flex min-w-[10rem] flex-col gap-1 text-xs font-medium text-gray-600">
                {f.label}
                <select
                  className={fieldClass}
                  value={values[f.name] ?? ''}
                  onChange={(e) => setField(f.name, e.target.value)}
                  required={f.required}
                >
                  {(f.options || []).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
            )
          }
          return (
            <label key={f.name} className="flex min-w-[12rem] flex-1 flex-col gap-1 text-xs font-medium text-gray-600">
              {f.label}
              <input
                type="text"
                className={fieldClass}
                value={values[f.name] ?? ''}
                placeholder={f.placeholder || ''}
                onChange={(e) => setField(f.name, e.target.value)}
                required={f.required}
              />
            </label>
          )
        })}
        <button type="submit" disabled={saving} className={btnPrimary}>
          {saving ? 'Saving…' : submitLabel}
        </button>
      </div>
      {error ? <p className="mt-2 text-xs text-rose-700">{error}</p> : null}
    </form>
  )
}
